import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { useCSVReader } from 'react-papaparse';
import ItemsService from "./Item.service.js";
import Alerts from '../../components/Alerts/alerts';
import LoadingOverlay from 'react-loading-overlay-ts';
import DNALoader from '../../components/Loaders/DnaLoader';

const BulkImportStockModal = ({ showModal, handleToggleModal, onSuccess, sessionID, itemId, businessId, pharmacyId }) => {
  const { t } = useTranslation();
  const [csvData, setCSVData] = useState([]);
  const { CSVReader } = useCSVReader();
  const [loading, setLoading] = useState(false);

  const handleOnUploadAccepted = (results) => {
    setCSVData(results.data);
  };

  const handleImport = async () => {
    if (csvData.length <= 1) {
      Alerts.swalErrorAlert(t('Please upload a valid CSV file'));
      return;
    }

    const stockData = csvData.slice(1).map(row => ({
      item_id: itemId,
      business_id: businessId,
      pharmacy_id: pharmacyId,
      quantity: row[0],
      price: row[1],
      expiry_date: row[2],
      status: 'Active'
    }));

    try {
       setLoading(true);
      const response = await ItemsService.AddBulkStock(sessionID, stockData);
      if (response.data.status) {
        setLoading(false);
        Alerts.swalSuccessAlert(t('Bulk stock added successfully'));
        onSuccess();
        handleToggleModal();
      } else {
        setLoading(false);
        Alerts.swalErrorAlert(response.data.message || t('Failed to add bulk stock'));
      }
    } catch (error) {
      setLoading(false);
      console.error('Error adding bulk stock:', error);
      Alerts.swalErrorAlert(t('An error occurred. Please try again.'));
    }
  };

  return (
    <Modal isOpen={showModal} toggle={handleToggleModal}>
      <ModalHeader toggle={handleToggleModal}>{t('Import Bulk Stock')}</ModalHeader>
      <LoadingOverlay
                active={loading}
                spinner={<DNALoader />}
              >
      <ModalBody>
        <CSVReader
          onUploadAccepted={handleOnUploadAccepted}
          config={{
            skipEmptyLines: true,
          }}
        >
          {({ getRootProps, acceptedFile, ProgressBar, getRemoveFileProps }) => (
            <>
              <div {...getRootProps()} style={styles.csvReader}>
                {acceptedFile ? (
                  <>
                    <div style={styles.acceptedFile}>
                      {acceptedFile.name}
                    </div>
                    <Button {...getRemoveFileProps()} style={styles.remove}>
                      Remove
                    </Button>
                  </>
                ) : (
                  t('Drop CSV file here or click to upload')
                )}
              </div>
              <ProgressBar style={styles.progressBar} />
            </>
          )}
        </CSVReader>
        <div className="mt-3">
          <small className="text-muted">
            {t('* Please do not remove or change the first row in the sample file.')}
          </small>
        </div>
        <div className="mt-3">
          <Button color="success" onClick={() => {
            const csvContent = "data:text/csv;charset=utf-8,quantity,price,expiry_date\n";
            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", "stock_import_sample.csv");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}>
            {t('Download Sample')}
          </Button>
        </div>
      </ModalBody>
      </LoadingOverlay>
      <ModalFooter>
      <div className="text-center mt-3">
        <Button color="btn btn-outline-success btn-hover-shine me-3" onClick={handleToggleModal}>{t('Cancel')}</Button>
        <Button color="btn btn-custom-color" onClick={handleImport}>{t('Import')}</Button>
      </div>
      </ModalFooter>
    </Modal>
  );
};

const styles = {
  csvReader: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 10,
    border: '1px solid #ccc',
    padding: 10,
    borderRadius: 4,
    cursor: 'pointer',
  },
  acceptedFile: {
    border: '1px solid #ccc',
    height: 45,
    lineHeight: 2.5,
    paddingLeft: 10,
    width: '80%',
  },
  remove: {
    borderRadius: 0,
    padding: '0 20px',
  },
  progressBar: {
    backgroundColor: 'red',
    height: 2,
  },
};

export default BulkImportStockModal;